import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { AttachMoney as AttachMoneyIcon, People as PeopleIcon, School as SchoolIcon, TrendingUp as TrendingUpIcon } from "@mui/icons-material";
import {
  getPrograms,
  getProgramCourses,
  addCourseToProgram,
  deleteCourse,
  updateCourse,
  getUnitSessionTypes,
  createUnitSessionType,
} from "../Components/enquiryProfileApi";
import { CurrencyContext } from "../Contexts/CurrencyContext";
import { RoleContext } from "../Contexts/RoleContext";
import DateRangeFilter, { getPresetRange } from "./marketing/DateRangeFilter";
import { getRevenueByProgram, getEnquiriesByProgram } from "./marketing/marketingApi";
import listStyles from "./AllEnquiries.module.scss";
import ProgramCard from "../Components/promotions/ProgramCard";

const COLORS = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed"];

/** Used only if the API is unreachable; server returns the same built-ins merged with DB rows. */
const FALLBACK_UNIT_SESSION_TYPES = [
  "Class",
  "Workshop",
  "Lecture",
  "Tutorial",
  "Lab",
  "Exam",
  "Mock exam",
  "Seminar",
  "Assessment",
  "Orientation",
];

function getSessionTypeChipColor(type) {
  if (!type) return "default";
  const t = (type || "").toLowerCase();
  if (t === "unit") return "success";
  if (t.includes("class")) return "primary";
  if (t.includes("exam")) return "secondary";
  if (t.includes("seminar")) return "info";
  return "default";
}

const COURSE_TYPES = [
  { value: "courses", label: "Course" },
  { value: "exam", label: "Exam" },
  { value: "seminars", label: "Seminar" },
  { value: "unit", label: "Unit" },
];

function newUnitSessionRow() {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    sessionType: "",
    durationHours: "",
    durationMinutes: "",
  };
}

function formatCourseTypeLabel(t) {
  const m = { courses: "Course", exam: "Exam", seminars: "Seminar", unit: "Unit" };
  return m[t] || t || "—";
}

export default function ProgramDetailPage() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, currencySymbol } = useContext(CurrencyContext);
  const { role } = useContext(RoleContext);
  const programsParentPath = role === "Back Office" ? "/programs/course-overview" : "/universities/list";
  const defaultDates = getPresetRange("This Month") || {};
  const [program, setProgram] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddCourse, setOpenAddCourse] = useState(false);
  const [openEditCourse, setOpenEditCourse] = useState(false);
  const [saving, setSaving] = useState(false);
  const [courseForm, setCourseForm] = useState({
    name: "",
    type: "courses",
    durationHours: "",
    durationMinutes: "",
    fees: "",
    unitSessions: [newUnitSessionRow()],
  });
  const [sessionKindOptions, setSessionKindOptions] = useState(FALLBACK_UNIT_SESSION_TYPES);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [openAddSessionKind, setOpenAddSessionKind] = useState(false);
  const [newSessionKindInput, setNewSessionKindInput] = useState("");
  const [savingSessionKind, setSavingSessionKind] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [revenueRow, setRevenueRow] = useState(null);
  const [enquiryRow, setEnquiryRow] = useState(null);
  const [dateRange, setDateRange] = useState({ date_from: defaultDates.start_date || "", date_to: defaultDates.end_date || "" });

  const loadProgram = useCallback(async () => {
    if (!programId) return;
    setLoading(true);
    try {
      const data = await getPrograms({});
      const programs = Array.isArray(data) ? data : (data?.programs || data?.universities || []);
      const p = programs.find((x) => String(x.id) === String(programId));
      setProgram(p || null);
    } catch (e) {
      toast.error(e.message || "Failed to load program.");
    } finally {
      setLoading(false);
    }
  }, [programId]);

  const loadCourses = useCallback(async () => {
    if (!programId) return;
    try {
      const data = await getProgramCourses(programId);
      setCourses(Array.isArray(data) ? data : (data?.courses || []));
    } catch (e) {
      toast.error(e.message || "Failed to load courses.");
    }
  }, [programId]);

  const loadInsights = useCallback(async () => {
    if (!programId) return;
    setInsightsLoading(true);
    try {
      const params = { start_date: dateRange.date_from, end_date: dateRange.date_to };
      const [revData, enqData] = await Promise.all([
        getRevenueByProgram(params),
        getEnquiriesByProgram(params),
      ]);
      const rev = Array.isArray(revData) ? revData.find((r) => String(r.program_id) === String(programId)) : null;
      const enq = Array.isArray(enqData) ? enqData.find((r) => String(r.program_id) === String(programId)) : null;
      setRevenueRow(rev || null);
      setEnquiryRow(enq || null);
    } catch (e) {
      toast.error(e.message || "Failed to load program insights.");
      setRevenueRow(null);
      setEnquiryRow(null);
    } finally {
      setInsightsLoading(false);
    }
  }, [programId, dateRange.date_from, dateRange.date_to]);

  useEffect(() => {
    loadProgram();
  }, [loadProgram]);

  useEffect(() => {
    if (programId) loadCourses();
  }, [programId, loadCourses]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const loadUnitSessionTypeOptions = useCallback(async () => {
    try {
      const types = await getUnitSessionTypes();
      setSessionKindOptions(Array.isArray(types) && types.length > 0 ? types : FALLBACK_UNIT_SESSION_TYPES);
    } catch {
      setSessionKindOptions(FALLBACK_UNIT_SESSION_TYPES);
    }
  }, []);

  useEffect(() => {
    loadUnitSessionTypeOptions();
  }, [loadUnitSessionTypeOptions]);

  const formatDuration = (durationValue) => {
    const totalMinutes = Number(durationValue);
    if (!Number.isFinite(totalMinutes)) return durationValue || "—";
    const safeMinutes = Math.max(0, Math.round(totalMinutes));
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const handleConfirmNewSessionKind = async () => {
    const t = newSessionKindInput.trim();
    if (!t) {
      toast.warning("Enter a name for the session type.");
      return;
    }
    setSavingSessionKind(true);
    try {
      await createUnitSessionType(t);
      await loadUnitSessionTypeOptions();
      setNewSessionKindInput("");
      setOpenAddSessionKind(false);
      toast.success("Session type saved.");
    } catch (e) {
      toast.error(e.message || "Could not save session type.");
    } finally {
      setSavingSessionKind(false);
    }
  };

  const handleAddCourse = async () => {
    if (!courseForm.name.trim()) {
      toast.warning("Course name is required.");
      return;
    }

    let payload;

    if (courseForm.type === "unit") {
      const session_components = [];
      for (let i = 0; i < courseForm.unitSessions.length; i++) {
        const row = courseForm.unitSessions[i];
        const st = (row.sessionType || "").trim();
        if (!st) {
          toast.warning(`Session ${i + 1}: select or enter a session type.`);
          return;
        }
        const h = Number(row.durationHours || 0);
        const m = Number(row.durationMinutes || 0);
        if (!Number.isFinite(h) || h < 0) {
          toast.warning(`Session ${i + 1}: hours must be a valid number.`);
          return;
        }
        if (!Number.isFinite(m) || m < 0 || m > 59) {
          toast.warning(`Session ${i + 1}: minutes must be between 0 and 59.`);
          return;
        }
        const mins = Math.round(h * 60 + m);
        if (mins <= 0) {
          toast.warning(`Session ${i + 1}: total contact time must be greater than zero.`);
          return;
        }
        session_components.push({ session_type: st, duration_minutes: mins });
      }
      if (session_components.length === 0) {
        toast.warning("Add at least one session with a type and duration.");
        return;
      }
      payload = {
        name: courseForm.name.trim(),
        type: "unit",
        session_components,
        fees: parseFloat(courseForm.fees) || 0,
      };
    } else {
      const hours = Number(courseForm.durationHours || 0);
      const minutes = Number(courseForm.durationMinutes || 0);
      if (!Number.isFinite(hours) || hours < 0) {
        toast.warning("Hours must be a valid number.");
        return;
      }
      if (!Number.isFinite(minutes) || minutes < 0 || minutes > 59) {
        toast.warning("Minutes must be between 0 and 59.");
        return;
      }
      const totalDurationMinutes = Math.round(hours * 60 + minutes);
      if (totalDurationMinutes <= 0) {
        toast.warning("Duration must be greater than zero.");
        return;
      }
      payload = {
        name: courseForm.name.trim(),
        type: courseForm.type,
        duration: String(totalDurationMinutes),
        fees: parseFloat(courseForm.fees) || 0,
      };
    }

    setSaving(true);
    try {
      await addCourseToProgram(programId, payload);
      toast.success("Course added.");
      setOpenAddCourse(false);
      setCourseForm({
        name: "",
        type: "courses",
        durationHours: "",
        durationMinutes: "",
        fees: "",
        unitSessions: [newUnitSessionRow()],
      });
      await loadCourses();
    } catch (e) {
      toast.error(e.message || "Failed to add course.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Remove this course?")) return;
    try {
      await deleteCourse(courseId);
      toast.success("Course removed.");
      await loadCourses();
    } catch (e) {
      toast.error(e.message || "Failed to remove course.");
    }
  };

  const handleOpenEditCourse = (course) => {
    const totalMinutes = Number(course?.duration || 0);
    const hours = Number.isFinite(totalMinutes) ? Math.floor(totalMinutes / 60) : 0;
    const minutes = Number.isFinite(totalMinutes) ? totalMinutes % 60 : 0;
    setEditingCourseId(course.id);
    setCourseForm({
      name: course?.name || "",
      type: course?.type || "courses",
      durationHours: String(hours || ""),
      durationMinutes: String(minutes || ""),
      fees: course?.fees != null ? String(course.fees) : "",
      unitSessions: Array.isArray(course?.session_components) && course.session_components.length > 0
        ? course.session_components.map((s, idx) => ({
            key: `${course.id}-${idx}-${Date.now()}`,
            sessionType: s.session_type || s.sessionType || "",
            durationHours: String(Math.floor(Number(s.duration_minutes || s.durationMinutes || 0) / 60)),
            durationMinutes: String(Number(s.duration_minutes || s.durationMinutes || 0) % 60),
          }))
        : [newUnitSessionRow()],
    });
    setOpenEditCourse(true);
  };

  const handleUpdateCourse = async () => {
    if (!editingCourseId) return;
    if (!courseForm.name.trim()) {
      toast.warning("Session name is required.");
      return;
    }
    let payload;
    if (courseForm.type === "unit") {
      const session_components = [];
      for (let i = 0; i < courseForm.unitSessions.length; i++) {
        const row = courseForm.unitSessions[i];
        const st = (row.sessionType || "").trim();
        if (!st) {
          toast.warning(`Session ${i + 1}: select or enter a session type.`);
          return;
        }
        const h = Number(row.durationHours || 0);
        const m = Number(row.durationMinutes || 0);
        const mins = Math.round(h * 60 + m);
        if (!Number.isFinite(mins) || mins <= 0) {
          toast.warning(`Session ${i + 1}: total contact time must be greater than zero.`);
          return;
        }
        session_components.push({ session_type: st, duration_minutes: mins });
      }
      payload = {
        name: courseForm.name.trim(),
        type: "unit",
        session_components,
        fees: parseFloat(courseForm.fees) || 0,
      };
    } else {
      const hours = Number(courseForm.durationHours || 0);
      const minutes = Number(courseForm.durationMinutes || 0);
      const totalDurationMinutes = Math.round(hours * 60 + minutes);
      if (!Number.isFinite(totalDurationMinutes) || totalDurationMinutes <= 0) {
        toast.warning("Duration must be greater than zero.");
        return;
      }
      payload = {
        name: courseForm.name.trim(),
        type: courseForm.type,
        duration: String(totalDurationMinutes),
        fees: parseFloat(courseForm.fees) || 0,
      };
    }

    setSaving(true);
    try {
      await updateCourse(editingCourseId, payload);
      toast.success("Session updated.");
      setOpenEditCourse(false);
      setEditingCourseId(null);
      await loadCourses();
    } catch (e) {
      toast.error(e.message || "Failed to update session.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !program) {
    return (
      <Box className={listStyles.root}>
        <Box className={listStyles.mainContent} sx={{ flex: 1, p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <IconButton onClick={() => navigate(programsParentPath)} aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
            <Skeleton variant="text" sx={{ flex: 1, maxWidth: 480, height: 40 }} />
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="text" width="55%" height={28} />
                    <Skeleton variant="text" width="35%" height={20} sx={{ mt: 0.5 }} />
                    <Skeleton variant="text" width="28%" height={24} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="92%" />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.paper", borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
              <Skeleton variant="text" width={160} height={32} />
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Skeleton variant="rounded" height={40} sx={{ width: { xs: "100%", sm: 280 } }} />
                <Skeleton variant="rounded" width={100} height={40} />
              </Stack>
            </Stack>
            <Grid container spacing={3}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} md={2.4} key={i}>
                  <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Skeleton variant="text" width={100} height={32} />
              <Skeleton variant="rounded" width={140} height={36} />
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[120, 72, 88, 64, 80].map((w, i) => (
                      <TableCell key={i}>
                        <Skeleton variant="text" width={w} height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((row) => (
                    <TableRow key={row}>
                      {[0, 1, 2, 3, 4].map((col) => (
                        <TableCell key={col}>
                          <Skeleton variant="text" width={col === 4 ? 72 : "90%"} height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (!program) {
    return (
      <Box className={listStyles.root}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2 }}>
          <IconButton onClick={() => navigate(programsParentPath)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography>Program not found.</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className={listStyles.root}>
        <Box className={listStyles.mainContent} sx={{ flex: 1, p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <IconButton onClick={() => navigate(programsParentPath)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ color: "var(--header-color, #111827)" }}>{program.name}</Typography>
          </Stack>

          <Box sx={{ mb: 2 }}>
            <ProgramCard program={program} formatCurrency={formatCurrency} />
          </Box>

          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.paper", borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
              <Typography variant="h6">Program insights</Typography>
              <Stack direction="row" spacing={2}>
                <DateRangeFilter value={dateRange} onChange={setDateRange} onApply={loadInsights} dateKeys={{ start: "date_from", end: "date_to" }} startLabel="Date from" endLabel="Date to" />
                <Button variant="contained" onClick={loadInsights} disabled={insightsLoading}>Refresh</Button>
              </Stack>
            </Stack>
            {insightsLoading ? (
              <Grid container spacing={3}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={2.4} key={i}>
                    <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
                  </Grid>
                ))}
              </Grid>
            ) : !revenueRow && !enquiryRow ? (
              <Typography color="text.secondary">No insights for this program in the selected date range.</Typography>
            ) : (
              <Grid container spacing={3}>
                {[
                  { label: "Students", value: revenueRow?.students ?? 0, icon: <PeopleIcon />, color: COLORS[0] },
                  { label: "Revenue", value: formatCurrency(revenueRow?.revenue), icon: <AttachMoneyIcon />, color: COLORS[2] },
                  { label: "Enquiries", value: enquiryRow?.enquiry_count ?? 0, icon: <SchoolIcon />, color: COLORS[1] },
                  { label: "Conversions", value: enquiryRow?.conversions ?? 0, icon: <TrendingUpIcon />, color: COLORS[1] },
                  { label: "Conversion Rate", value: enquiryRow?.conversion_rate != null ? `${enquiryRow.conversion_rate}%` : "0%", icon: <TrendingUpIcon />, color: COLORS[3] },
                ].map((item, idx) => (
                  <Grid item xs={12} sm={6} md={2.4} key={idx}>
                    <Card sx={{ height: "100%", "&:hover": { boxShadow: 6 } }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                          <Avatar sx={{ bgcolor: item.color, width: 40, height: 40 }}>{item.icon}</Avatar>
                        </Stack>
                        <Typography variant="h5" sx={{ mt: 2, fontWeight: 600 }}>{item.value}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Sessions</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCourseForm({
                    name: "",
                    type: "courses",
                    durationHours: "",
                    durationMinutes: "",
                    fees: "",
                    unitSessions: [newUnitSessionRow()],
                  });
                  loadUnitSessionTypeOptions();
                  setOpenAddCourse(true);
                }}
              >
                Add Session
              </Button>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Fees</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {courses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No sessions yet. Add one above.
                      </TableCell>
                    </TableRow>
                  ) : (
                    courses.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatCourseTypeLabel(c.type)}
                            color={getSessionTypeChipColor(c.type)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <span>{formatDuration(c.duration)}</span>
                            {String(c.type).toLowerCase() === "unit" &&
                              Array.isArray(c.session_components) &&
                              c.session_components.length > 0 && (
                                <Tooltip
                                  title={
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                      {c.session_components.map((s, idx) => (
                                        <li key={idx}>
                                          <Typography variant="caption" component="span">
                                            {(s.session_type || s.sessionType || "—")}: total{" "}
                                            {formatDuration(s.duration_minutes ?? s.durationMinutes)}
                                          </Typography>
                                        </li>
                                      ))}
                                    </Box>
                                  }
                                  arrow
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 18, color: "action.active", cursor: "help" }} />
                                </Tooltip>
                              )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {c.discount_percentage > 0 ? (
                            <Stack direction="row" justifyContent="flex-end" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                {formatCurrency(c.original_price ?? c.fees)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "success.main", fontWeight: 700 }}>
                                {formatCurrency(c.discounted_price ?? c.fees)}
                              </Typography>
                              <Chip size="small" color="error" label={`${c.discount_percentage}% OFF`} />
                            </Stack>
                          ) : (
                            <>{c.fees != null ? formatCurrency(c.fees) : "—"}</>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditCourse(c)}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteCourse(c.id)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

      <Dialog open={openAddCourse} onClose={() => setOpenAddCourse(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Session</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Session name"
              value={courseForm.name}
              onChange={(e) => setCourseForm((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={courseForm.type}
                label="Type"
                onChange={(e) => {
                  const next = e.target.value;
                  setCourseForm((prev) => ({
                    ...prev,
                    type: next,
                    ...(next === "unit" && (!prev.unitSessions || prev.unitSessions.length === 0)
                      ? { unitSessions: [newUnitSessionRow()] }
                      : {}),
                  }));
                }}
              >
                {COURSE_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {courseForm.type === "unit" ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Add one row per session type (e.g. Class, Lab). For each row, enter the total contact time students must complete
                  for that type (sum of all meetings, including repeats when you schedule with free pick). Fee below is for the whole
                  unit. Use + on a row to save a new session type label in the database.
                </Typography>
                <Stack spacing={2}>
                  {courseForm.unitSessions.map((row, idx) => (
                    <Paper key={row.key} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1.5}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
                          <Autocomplete
                            freeSolo
                            fullWidth
                            options={sessionKindOptions}
                            value={row.sessionType ?? ""}
                            onChange={(_, v) => {
                              const val = v == null ? "" : String(v);
                              setCourseForm((prev) => {
                                const next = [...prev.unitSessions];
                                next[idx] = { ...next[idx], sessionType: val };
                                return { ...prev, unitSessions: next };
                              });
                            }}
                            onInputChange={(_, v, reason) => {
                              if (reason === "input") {
                                setCourseForm((prev) => {
                                  const next = [...prev.unitSessions];
                                  next[idx] = { ...next[idx], sessionType: v };
                                  return { ...prev, unitSessions: next };
                                });
                              }
                            }}
                            renderInput={(params) => (
                              <TextField {...params} label="Session type" placeholder="Class, Lab, …" />
                            )}
                          />
                          <Tooltip title="Add a new session type to the database">
                            <IconButton
                              color="primary"
                              onClick={() => setOpenAddSessionKind(true)}
                              sx={{ mt: { sm: 0.5 } }}
                              aria-label="Add session type preset"
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            color="error"
                            disabled={courseForm.unitSessions.length <= 1}
                            onClick={() =>
                              setCourseForm((prev) => ({
                                ...prev,
                                unitSessions: prev.unitSessions.filter((_, j) => j !== idx),
                              }))
                            }
                            aria-label="Remove row"
                            sx={{ mt: { sm: 0.5 } }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Stack>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 0.25 }}
                            label="Total contact (hours)"
                            value={row.durationHours}
                            onChange={(e) => {
                              const v = e.target.value;
                              setCourseForm((prev) => {
                                const next = [...prev.unitSessions];
                                next[idx] = { ...next[idx], durationHours: v };
                                return { ...prev, unitSessions: next };
                              });
                            }}
                            placeholder="e.g. 1.5"
                          />
                          <TextField
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, max: 59, step: 1 }}
                            label="Total contact (minutes)"
                            value={row.durationMinutes}
                            onChange={(e) => {
                              const v = e.target.value;
                              setCourseForm((prev) => {
                                const next = [...prev.unitSessions];
                                next[idx] = { ...next[idx], durationMinutes: v };
                                return { ...prev, unitSessions: next };
                              });
                            }}
                            placeholder="e.g. 30"
                          />
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setCourseForm((prev) => ({
                      ...prev,
                      unitSessions: [...prev.unitSessions, newUnitSessionRow()],
                    }))
                  }
                >
                  Add session row
                </Button>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  label="Unit fee (overall)"
                  value={courseForm.fees}
                  onChange={(e) => setCourseForm((prev) => ({ ...prev, fees: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">{currencySymbol || "Rs."}</InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 0.25 }}
                    label="Duration hours"
                    value={courseForm.durationHours}
                    onChange={(e) => setCourseForm((prev) => ({ ...prev, durationHours: e.target.value }))}
                    placeholder="e.g. 1.5"
                  />
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, max: 59, step: 1 }}
                    label="Duration minutes"
                    value={courseForm.durationMinutes}
                    onChange={(e) => setCourseForm((prev) => ({ ...prev, durationMinutes: e.target.value }))}
                    placeholder="e.g. 30"
                  />
                </Stack>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  label="Fees"
                  value={courseForm.fees}
                  onChange={(e) => setCourseForm((prev) => ({ ...prev, fees: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">{currencySymbol || "Rs."}</InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddCourse(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddCourse} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddSessionKind} onClose={() => !savingSessionKind && setOpenAddSessionKind(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New session type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            value={newSessionKindInput}
            onChange={(e) => setNewSessionKindInput(e.target.value)}
            placeholder="e.g. Field trip"
            disabled={savingSessionKind}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConfirmNewSessionKind();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddSessionKind(false)} disabled={savingSessionKind}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmNewSessionKind} disabled={savingSessionKind}>
            {savingSessionKind ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openEditCourse} onClose={() => setOpenEditCourse(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Session</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Session name"
              value={courseForm.name}
              onChange={(e) => setCourseForm((prev) => ({ ...prev, name: e.target.value }))}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={courseForm.type}
                label="Type"
                onChange={(e) => {
                  const next = e.target.value;
                  setCourseForm((prev) => ({
                    ...prev,
                    type: next,
                    ...(next === "unit" && (!prev.unitSessions || prev.unitSessions.length === 0)
                      ? { unitSessions: [newUnitSessionRow()] }
                      : {}),
                  }));
                }}
              >
                {COURSE_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {courseForm.type === "unit" ? (
              <Typography variant="body2" color="text.secondary">
                Update unit session rows with total contact time per session type.
              </Typography>
            ) : (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.25 }}
                  label="Duration hours"
                  value={courseForm.durationHours}
                  onChange={(e) => setCourseForm((prev) => ({ ...prev, durationHours: e.target.value }))}
                />
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 59, step: 1 }}
                  label="Duration minutes"
                  value={courseForm.durationMinutes}
                  onChange={(e) => setCourseForm((prev) => ({ ...prev, durationMinutes: e.target.value }))}
                />
              </Stack>
            )}
            <TextField
              fullWidth
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              label="Fees"
              value={courseForm.fees}
              onChange={(e) => setCourseForm((prev) => ({ ...prev, fees: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{currencySymbol || "Rs."}</InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditCourse(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateCourse} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
